import RouteTemplate from 'ember-route-template'
import type ReLessonPerformanceRoute from 'teacher-ui-client/reporting/ui-scope/re/lesson-performance/route'
import type ReLessonPerformanceController from 'teacher-ui-client/reporting/ui-scope/re/lesson-performance/controller'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { t } from 'ember-intl'
import Component from '@glimmer/component'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import { service } from '@ember/service'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import ReportingControls from 'teacher-ui-client/components/reporting/controls'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { ReportingControlsGap } from 'teacher-ui-client/components/reporting/controls-gap'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { StudentSearchComponent } from 'teacher-ui-client/components/student-search'
import { ReportingPrintButton } from 'teacher-ui-client/components/reporting/print-button'
import {
  PerformanceOverview,
  type LessonsOverviewReportData,
} from 'teacher-ui-client/components/reporting/performance-overview'
import type SessionService from 'teacher-ui-client/services/session'
import {
  LessonSelectionPanel,
  type LessonSelectionData,
  type LessonStandard,
} from 'teacher-ui-client/components/reporting/lesson-performance/lesson-selection-panel'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { exportFilename } from 'teacher-ui-client/services/reporting'
import { task } from 'ember-concurrency'
import {
  LessonResults,
  type ResultsReportData,
} from 'teacher-ui-client/components/reporting/lesson-performance/results'
import { and } from 'ember-truth-helpers'
import NoReportValue from 'teacher-ui-client/components/reporting/no-report-value'
import { array } from '@ember/helper'
import { NoStandardsSetAlert } from 'teacher-ui-client/components/standards-set/no-standards-set-alert'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { currentStandardsSetUnsupported } from 'teacher-ui-client/helpers/standards-set'
import { StandardsSetUnsupportedAlert } from 'teacher-ui-client/components/standards-set/unsupported-alert'

interface Signature {
  Args: {
    model: ModelFor<ReLessonPerformanceRoute>
    controller: ReLessonPerformanceController
  }
}

export class ReLessonPerformance extends Component<Signature> {
  @service reporting!: ReportingService
  @service lessonInfo!: LessonInfoService
  @service session!: SessionService

  fetchLessonSelection = trackedFunction(this, () => {
    return this.lessonInfo.fetchLessonStandards<LessonSelectionData>({
      product: Product.RE,
      standardsSetId: this.session.currentSchool.standardsSet?.id ?? null,
    })
  })

  fetchOverviewReport = trackedFunction(this, () => {
    const url = this.reporting.buildGravityUrl({
      product: Product.RE,
      requestScope: this.args.model.requestScope,
      reportName: 'lesson-performance/overview',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      filter: { lesson: this.args.controller.selectedLesson },
    })

    return this.reporting.fetchReport<LessonsOverviewReportData>(url, query)
  })

  fetchResultsReport = trackedFunction(this, () => {
    const url = this.reporting.buildGravityUrl({
      product: Product.RE,
      requestScope: this.args.model.requestScope,
      reportName: 'lesson-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      filter: { lesson: this.args.controller.selectedLesson },
      page: { number: this.args.controller.pageNumber, size: this.args.controller.perPage },
      sort: this.args.controller.sort,
    })

    return this.reporting.fetchReport<ResultsReportData>(url, query)
  })

  @use
  latestLessonData = keepLatest({
    value: () => this.fetchLessonSelection.value,
    when: () => this.fetchLessonSelection.isPending,
  })

  @use
  latestOverviewData = keepLatest({
    value: () => this.fetchOverviewReport.value,
    when: () => this.fetchOverviewReport.isPending,
  })

  @use
  latestResultsData = keepLatest({
    value: () => this.fetchResultsReport.value,
    when: () => this.fetchResultsReport.isPending,
  })

  get lessonData() {
    return this.latestLessonData?.lesson_standards.length === 0 ? undefined : this.latestLessonData
  }

  get overviewData() {
    return this.latestOverviewData?.overview.total_students ? this.latestOverviewData : undefined
  }

  exportResultsCsv = task({ drop: true }, async () => {
    const url = this.reporting.buildGravityUrl({
      product: Product.RE,
      requestScope: this.args.model.requestScope,
      reportName: 'lesson-performance/results',
    })
    const query = this.reporting.buildQuery({
      period: this.args.model.period,
      filter: { lesson: this.args.controller.selectedLesson },
      sort: this.args.controller.sort,
    })
    const filename = exportFilename(Product.RE, 'lesson-performance', '.csv')

    await this.reporting.exportReportCsv(url, query, filename)
  })

  updateSelectedLesson = (lesson: number) => {
    this.args.controller.pageNumber = 1
    this.args.controller.selectedLesson = lesson
  }

  get selectedLessonData(): LessonStandard | undefined {
    return this.latestLessonData?.lesson_standards.find(
      (lessonItem) => lessonItem.lesson === this.args.controller.selectedLesson,
    )
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  get isMissingStandards() {
    return !this.session.currentSchool.standardsSet
  }

  get isOneRosterEnabled() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.pageTitles.lessonPerformance"}} />
    <ReportingControls>
      <ReportingUiScopeSelect class="shrink basis-80" @disabled={{this.isMissingStandards}} />
      <ReportingControlsGap />
      <ReportingPeriodSelect @disabled={{this.isMissingStandards}} />
      <ReportingPrintButton @disabled={{this.isMissingStandards}} />
      <ReportingControlsGap />
      <StudentSearchComponent
        @studentRouteName="reporting.ui-scope.re.student-summary"
        @students={{@model.scopedStudents}}
        @includeSelectAll={{false}}
      />
    </ReportingControls>
    <div class="space-y-6">
      {{#if (currentStandardsSetUnsupported)}}
        <StandardsSetUnsupportedAlert />
      {{else}}
        {{#if this.isMissingStandards}}
          <NoStandardsSetAlert
            @hasDisco={{Boolean this.session.currentSchool.district.districtCoordinators.length}}
            @isSubco={{this.isSubco}}
            @isOneRosterEnabled={{this.isOneRosterEnabled}}
          />
        {{else}}
          {{#if this.lessonData}}
            <LessonSelectionPanel
              @lessonData={{this.lessonData.lesson_standards}}
              @selectedLesson={{@controller.selectedLesson}}
              @setLesson={{this.updateSelectedLesson}}
              @standardsSet={{this.session.currentSchool.standardsSet.name}}
            />
          {{/if}}
          {{#if (and this.lessonData this.overviewData this.latestResultsData)}}
            {{#if this.overviewData}}
              <PerformanceOverview
                @reportData={{this.overviewData}}
                @isSubco={{this.isSubco}}
                @isReloading={{this.fetchOverviewReport.isPending}}
              />
            {{/if}}
            {{#if this.latestResultsData}}
              <LessonResults
                @reportData={{this.latestResultsData}}
                @selectedLessonData={{this.selectedLessonData}}
                @exportCsv={{this.exportResultsCsv}}
                @isSubco={{this.isSubco}}
                @isReloading={{this.fetchResultsReport.isPending}}
                @product={{Product.RE}}
              />
            {{/if}}
          {{else}}
            <NoReportValue @reportStates={{array this.fetchOverviewReport this.fetchResultsReport}} />
          {{/if}}
        {{/if}}
      {{/if}}
    </div>
  </template>
}

export default RouteTemplate(ReLessonPerformance)
